import { useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import SubmitButton from './partials/SubmitButton';
import ErrorMessage from './partials/ErrorMessage';
import TextLink from './partials/TextLink';

import { MSDIcon } from '../../misc/icons/Icons';
import Inputfield from '../../misc/Inputfield';
import useTranslation from '../../../hooks/useTranslations';
import { register, selectRegisterError, selectRegisterStatus } from '../../../redux/modules/authSlice';

const RegisterScreen = () => {
	const t = useTranslation();
	const dispatch = useDispatch();

	const registerStatus = useSelector((state) => selectRegisterStatus(state));
	const registerError = useSelector((state) => selectRegisterError(state));

	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [email, setEmail] = useState('');
	const [emailConfirmation, setEmailConfirmation] = useState('');
	const [company, setCompany] = useState('');

	const [firstNameError, setFirstNameError] = useState(null);
	const [lastNameError, setLastNameError] = useState(null);
	const [emailError, setEmailError] = useState(null);
	const [emailConfirmationError, setEmailConfirmationError] = useState(null);
	const [companyError, setCompanyError] = useState(null);

	const validateFields = () => {
		setFirstNameError(null);
		setLastNameError(null);
		setEmailError(null);
		setEmailConfirmationError(null);
		setCompanyError(null);

		let valid = true;

		if (!firstName) {
			setFirstNameError(t('register_validation_first_name'));
			valid = false;
		}
		if (!lastName) {
			setLastNameError(t('register_validation_last_name'));
			valid = false;
		}
		if (!email) {
			setEmailError(t('register_validation_mail'));
			valid = false;
		} else if (!(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(String(email).toLowerCase()))) {
			setEmailError(t('register_validation_mail_not_valid'));
			valid = false;
		}

		if (!emailConfirmation) {
			setEmailConfirmationError(t('register_validation_mail_confirm'));
			valid = false;
		} else if (!(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(String(emailConfirmation).toLowerCase()))) {
			setEmailConfirmationError(t('register_validation_mail_not_valid'));
			valid = false;
		} else if (emailConfirmation !== email) {
			setEmailConfirmationError(t('register_validation_mail_difference'));
			valid = false;
		}

		if (!company) {
			setCompanyError(t('register_validation_company'));
			valid = false;
		}

		if (!valid) {
			return false;
		}
		return true;
	};

	const handleRegister = () => {
		if (validateFields()) {
			dispatch(register({ firstName, lastName, email, company }));
		}
	};

	// if successKey == "user_activated" {
	// 	message = t("register_success_activation")
	// } else if successKey == "user_deactivated" {
	// 	message = t("register_success_distributor")
	// }

	return (
		<div className="flex flex-col justify-center min-h-screen py-12 sm:pb-48 sm:px-6 lg:px-8">
			<div className="px-4 sm:mx-auto sm:w-full sm:max-w-md">
				<MSDIcon className="mx-auto w-42 h-14 sm:w-60 sm:h-20 text-oly-blue-dark" />
				<h2 className="mt-8 font-bold text-center text-[1.5625rem] text-oly-gray-darkest">
					{t('register_headline')}
				</h2>
				{registerStatus === 'success' && (
					<>
						<h3 className="mt-2 font-bold text-center text-[1.0625rem] text-oly-gray-darkest">
							{t('register_success_activation')}
						</h3>
						<div className="flex items-center justify-center mx-1 mt-8">
							<TextLink href="/login" text={t('webapp_login_back')} title={t('general_back')} />
						</div>
					</>
				)}
			</div>
			{registerStatus !== 'success' && (
				<div className="mx-4 mt-8 sm:mx-auto sm:w-full sm:max-w-2xl">
					<div className="px-4 py-8 bg-white rounded-sm shadow sm:px-10">
						<form action="#" method="POST" onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); }}>
							<div className="flex flex-col gap-y-4 sm:gap-y-6">
								<div className="flex flex-col gap-y-4 sm:flex-row sm:gap-x-4 sm:gap-y-0">
									<div className="w-full">
										<Inputfield fieldName="firstName" label={t('register_first_name_placeholder')} type="text" initialValue={firstName} placeholder={t('register_validation_first_name')} handleChange={setFirstName} showLabel />
										{firstNameError && <ErrorMessage message={firstNameError} />}
									</div>
									<div className="w-full">
										<Inputfield fieldName="lastName" label={t('register_last_name_placeholder')} type="text" initialValue={lastName} placeholder={t('register_validation_last_name')} handleChange={setLastName} showLabel />
										{lastNameError && <ErrorMessage message={lastNameError} />}
									</div>
								</div>

								<div className="flex flex-col gap-y-4 sm:flex-row sm:gap-x-4 sm:gap-y-0">
									<div className="w-full">
										<Inputfield fieldName="email" label={t('register_mail_placeholder')} type="text" initialValue={email} placeholder={t('register_validation_mail')} handleChange={setEmail} showLabel />
										{emailError && <ErrorMessage message={emailError} />}
									</div>
									<div className="w-full">
										<Inputfield fieldName="emailConfirmation" label={t('register_mail_confirm_placeholder')} type="text" initialValue={emailConfirmation} placeholder={t('register_validation_mail_confirm')} handleChange={setEmailConfirmation} showLabel />
										{emailConfirmationError && <ErrorMessage message={emailConfirmationError} />}
									</div>
								</div>

								<div className="flex flex-col gap-y-4 sm:flex-row sm:gap-x-4 sm:gap-y-0">
									<div className="w-full">
										<Inputfield fieldName="company" label={t('register_company_placeholder')} type="text" initialValue={company} placeholder={t('register_validation_company')} handleChange={setCompany} showLabel />
										<div className="text-xs my-1 px-1">{t('register_company_info')}</div>
										{companyError && <ErrorMessage message={companyError} />}
									</div>
								</div>
							</div>

							<div className="flex items-center justify-between mx-1 mt-6">
								{process.env.REACT_APP_LINKS_IMPRINT && process.env.REACT_APP_LINKS_IMPRINT !== '' && (<TextLink href={process.env.REACT_APP_LINKS_IMPRINT} text={t('webapp_footer_imprint')} title={t('webapp_footer_imprint')} external color="text-oly-gray-darkest" />)}
								{process.env.REACT_APP_LINKS_PRIVACY && process.env.REACT_APP_LINKS_PRIVACY !== '' && (<TextLink href={process.env.REACT_APP_LINKS_PRIVACY} text={t('webapp_footer_privacy')} title={t('webapp_footer_privacy')} external color="text-oly-gray-darkest" />)}
							</div>

							<div className="mx-auto mt-6 sm:px-10 sm:max-w-md">
								<SubmitButton type="submit" title={t('register_register_button')} loading={registerStatus === 'pending'} handleClick={handleRegister} />
								{registerError && <ErrorMessage message={t(registerError)} />}
							</div>
						</form>
						<div className="flex items-center justify-between mx-1 mt-8">
							<TextLink href="/login" text={t('webapp_login_back')} title={t('general_back')} />
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default RegisterScreen;
